import { useNavigation } from '@/shared/hooks';
import {
  DataTable,
  Menu,
  Pagination,
  Tag,
  type DataTableColumn,
} from '@noorden-insurtech/alba-design-system';
import { AlertCircle, CalendarDays, Eye, Hash, RefreshCw } from 'lucide-react';
import { useMemo } from 'react';
import { CopyableField, SearchFilterField, SelectFilterField } from '../../../shared/ui';
import { f, useFilters, formatDateTimeToBrazil } from '../../../shared/utils';
import { useIntegrations } from '../hooks/use-integrations.hook';
import type { EmissionControl } from '../../../services/integrations/types';
// Alias curto de propósito: os nomes exportados são longos e, usados direto no
// JSX, estouram o printWidth de 80 e forçam o prettier a reflowar a tabela toda.
import {
  EMISSION_CONTROL_STATUS_CONFIG as statusConfig,
  EMISSION_CONTROL_STATUS_OPTIONS as statusOptions,
  EMISSION_CONTROL_STEP_LABELS as stepLabels,
} from '../constants/emission-control-labels.constants';

const stepOptions = [
  { label: 'Todos', value: '' },
  { label: 'Inicial', value: 'initial' },
  { label: 'Segurado I4Pro', value: 'insurer_i4pro' },
  { label: 'Apólice I4Pro', value: 'policy_i4pro' },
  { label: 'Apólice InsureMO', value: 'policy_insuremo' },
  { label: 'PDF Apólice', value: 'policy_pdf' },
  { label: 'PDF Bucket', value: 'policy_pdf_bucket' },
  { label: 'E-mail Cliente', value: 'email_customer' },
  { label: 'Envio ECM I4Pro', value: 'ecm_i4pro' },
  { label: 'Cobrança (link de pagamento)', value: 'payment_charge' },
  { label: 'E-mail Interno', value: 'internal_email' },
  { label: 'Finalizado', value: 'finished' },
];

function createColumns(
  onAction: (action: 'details', item: EmissionControl) => void,
): DataTableColumn<EmissionControl>[] {
  return [
    {
      accessorKey: 'insuremoPolicyId',
      header: 'ID APÓLICE INSUREMO',
      size: 160,
      cell: ({ row }) => (
        <CopyableField value={row.original.insuremoPolicyId} label="ID Apólice InsureMO" truncate />
      ),
    },
    {
      accessorKey: 'proposalNo',
      header: 'Nº PROPOSTA',
      size: 150,
      cell: ({ row }) => row.original.proposalNo
        ? <CopyableField value={row.original.proposalNo} label="Nº Proposta" />
        : <span className='text-alba-gray-700 text-sm'>-</span>,
    },
    {
      accessorKey: 'i4proPolicyNo',
      header: 'Nº APÓLICE I4PRO',
      size: 150,
      cell: ({ row }) => row.original.i4proPolicyNo
        ? <CopyableField value={row.original.i4proPolicyNo} label="Nº Apólice I4Pro" />
        : <span className='text-alba-gray-700 text-sm'>-</span>,
    },
    {
      accessorKey: 'status',
      header: 'STATUS',
      size: 130,
      cell: ({ row }) => {
        const config = statusConfig[row.original.status] ?? {
          label: row.original.status,
          colorScheme: 'gray' as const,
        };
        return (
          <Tag.Root variant='outline' colorScheme={config.colorScheme} size='sm'>
            <Tag.Label>{config.label}</Tag.Label>
          </Tag.Root>
        );
      },
    },
    {
      accessorKey: 'step',
      header: 'ETAPA',
      size: 150,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm'>
          {stepLabels[row.original.step] ?? row.original.step}
        </span>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'CRIADO EM',
      size: 150,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm'>
          {formatDateTimeToBrazil(row.original.createdAt)}
        </span>
      ),
    },
    {
      accessorKey: 'errorMessage',
      header: 'ERRO',
      size: 200,
      cell: ({ row }) => {
        const msg = row.original.errorMessage;
        if (!msg) return <span className='text-alba-gray-400 text-sm'>-</span>;
        return (
          <span className='text-red-600 text-sm truncate block max-w-[200px]' title={msg}>
            {msg}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      size: 50,
      cell: ({ row }) => (
        <div className='flex justify-end'>
          <Menu.Root>
            <Menu.Trigger />
            <Menu.Content>
              <Menu.Item
                value='details'
                icon={<Eye size={16} />}
                onSelect={() => onAction('details', row.original)}
              >
                Ver detalhes
              </Menu.Item>
            </Menu.Content>
          </Menu.Root>
        </div>
      ),
    },
  ];
}

export function IntegrationsPage() {
  const { push } = useNavigation();
  const { filters, setFilters, registerFilter } = useFilters({
    search: f.string().withDefault('').withDebounce(500),
    status: f.string().withDefault(''),
    step: f.string().withDefault(''),
    pageNo: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(50),
  });

  const { data, isLoading, refetch, isFetching } = useIntegrations({
    pageNumber: filters.pageNo,
    pageSize: filters.pageSize,
    status: filters.status || undefined,
    step: filters.step || undefined,
    search: filters.search || undefined,
  });

  const handleAction = (action: 'details', item: EmissionControl) => {
    if (action === 'details') {
      push('/integrations/$policyId', { policyId: item.insuremoPolicyId });
    }
  };

  const columns = useMemo(() => createColumns(handleAction), []);
  const items = data?.items ?? [];

  return (
    <div className='space-y-4 mt-4'>
      <div className='flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center'>
        <SearchFilterField
          {...registerFilter('search')}
          placeholder='Buscar por ID da apólice, nº da proposta ou nº da apólice I4Pro'
          className='w-full md:flex-1 md:min-w-[320px]'
        />
        <SelectFilterField
          {...registerFilter('status')}
          items={statusOptions}
          placeholder='Status'
          className='w-full md:w-auto md:min-w-[160px]'
          onValueChange={value => setFilters({ ...filters, status: value })}
        />
        <div className='flex w-full items-center gap-2 md:w-auto md:contents'>
          <SelectFilterField
            {...registerFilter('step')}
            items={stepOptions}
            placeholder='Etapa'
            className='w-full md:w-auto md:min-w-[180px]'
            onValueChange={value => setFilters({ ...filters, step: value })}
          />
          <button
            type='button'
            onClick={() => refetch()}
            disabled={isFetching}
            className='flex h-11 w-12 shrink-0 items-center justify-center rounded-lg border border-alba-gray-300 bg-white text-alba-gray-600 transition-colors hover:bg-alba-gray-50 hover:text-alba-gray-700 disabled:opacity-50 md:w-auto md:px-3'
            title='Atualizar'
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className='space-y-4 md:hidden'>
        {isLoading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className='h-60 animate-pulse rounded-3xl bg-alba-gray-200'
            />
          ))
        ) : (
          items.map(item => {
            const config = statusConfig[item.status] ?? {
              label: item.status,
              colorScheme: 'gray' as const,
            };
            return (
              <article
                key={`${item.insuremoPolicyId}-${item.createdAt}`}
                className='rounded-3xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'
              >
                <div className='flex items-start justify-between gap-3'>
                  <div className='min-w-0'>
                    <p className='truncate font-gotham font-bold text-alba-black-500'>
                      {item.insuremoPolicyId}
                    </p>
                    <p className='mt-1 text-xs text-alba-gray-600'>
                      ID Apólice InsureMO
                    </p>
                  </div>
                  <Menu.Root>
                    <Menu.Trigger />
                    <Menu.Content>
                      <Menu.Item
                        value='details'
                        icon={<Eye size={16} />}
                        onSelect={() => handleAction('details', item)}
                      >
                        Ver detalhes
                      </Menu.Item>
                    </Menu.Content>
                  </Menu.Root>
                </div>

                <div className='mt-4 space-y-3'>
                  <div>
                    <p className='mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                      <Hash className='h-3.5 w-3.5' />
                      Nº Proposta
                    </p>
                    <p className='text-sm text-alba-gray-700'>{item.proposalNo || '-'}</p>
                  </div>
                  <div className='h-px w-full bg-alba-gray-200' />
                  <div>
                    <p className='mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                      <Hash className='h-3.5 w-3.5' />
                      Nº Apólice I4Pro
                    </p>
                    <p className='text-sm text-alba-gray-700'>{item.i4proPolicyNo || '-'}</p>
                  </div>
                  <div className='h-px w-full bg-alba-gray-200' />
                  <div>
                    <p className='mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                      <Hash className='h-3.5 w-3.5' />
                      Etapa
                    </p>
                    <p className='text-sm text-alba-gray-700'>
                      {stepLabels[item.step] ?? item.step}
                    </p>
                  </div>
                  <div className='h-px w-full bg-alba-gray-200' />
                  <div>
                    <p className='mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                      <Hash className='h-3.5 w-3.5' />
                      Status
                    </p>
                    <Tag.Root variant='outline' colorScheme={config.colorScheme} size='sm'>
                      <Tag.Label>{config.label}</Tag.Label>
                    </Tag.Root>
                  </div>
                  <div className='h-px w-full bg-alba-gray-200' />
                  <div>
                    <p className='mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                      <CalendarDays className='h-3.5 w-3.5' />
                      Criado em
                    </p>
                    <p className='text-sm text-alba-gray-700'>
                      {formatDateTimeToBrazil(item.createdAt)}
                    </p>
                  </div>
                  {item.errorMessage && (
                    <div>
                      <div className='mb-3 h-px w-full bg-alba-gray-200' />
                      <p className='mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-alba-gray-600'>
                        <AlertCircle className='h-3.5 w-3.5' />
                        Erro
                      </p>
                      <p className='text-sm text-red-600'>{item.errorMessage}</p>
                    </div>
                  )}
                </div>
              </article>
            );
          })
        )}

        {!isLoading && !items.length && (
          <div className='rounded-3xl bg-white p-6 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
            Nenhuma integração encontrada.
          </div>
        )}

        {(data?.meta.totalPages ?? 0) > 0 && (
          <div className='origin-center scale-90'>
            <Pagination
              className='justify-center gap-1 [&>div]:gap-1'
              count={(data?.meta.totalPages ?? 0) * filters.pageSize}
              page={filters.pageNo}
              pageSize={filters.pageSize}
              totalItems={data?.meta.total ?? 0}
              pageSizeOptions={[10, 25, 50, 100]}
              onPageChange={p =>
                setFilters({ ...filters, pageNo: p.page, pageSize: p.pageSize })
              }
              onPageSizeSelect={pageSize =>
                setFilters({ ...filters, pageNo: 1, pageSize })
              }
            />
          </div>
        )}
      </div>

      <div className='hidden min-w-0 overflow-hidden rounded-3xl md:block'>
        <DataTable
          className='min-w-0'
          loading={isLoading}
          columns={columns}
          data={items}
          totalItems={data?.meta.total ?? 0}
          totalPages={data?.meta.totalPages ?? 0}
          pageSize={filters.pageSize}
          pageSizeOptions={[10, 25, 50, 100]}
          onPageChange={(page, pageSize) =>
            setFilters({ ...filters, pageNo: page, pageSize })
          }
        />
      </div>
    </div>
  );
}
